// WorldMapOverlay.js - 大地图总览（按 M 打开）
import UIStyleHelper from './UIStyleHelper.js';
import { findChunkEntry, globalTileToChunk, pixelToGlobalTile } from './WorldChunkCoords.js';

const WORLD_LANDMARKS = [
    { gridX: 1, gridY: 4, localTileX: 18, localTileY: 25, label: '拉多镇', type: 'town' },
    { gridX: 1, gridY: 4, localTileX: 24, localTileY: 14, label: '拉多北山洞', type: 'cave' },
    { gridX: 1, gridY: 4, localTileX: 26, localTileY: 32, label: '拉多南山洞', type: 'cave' },
    { gridX: 1, gridY: 1, localTileX: 62, localTileY: 18, label: '麦基镇', type: 'town' },
    { gridX: 3, gridY: 2, localTileX: 55, localTileY: 42, label: '弗里镇', type: 'town' },
    { gridX: 3, gridY: 3, localTileX: 60, localTileY: 38, label: '湖中镇', type: 'town' },
    { gridX: 4, gridY: 3, localTileX: 35, localTileY: 55, label: '东南港', type: 'town' }
];

/** 按区块行列返回地貌配色 */
function getRegionStyle(row, col) {
    if (row === 0) {
        if (col === 1 || col === 2) {
            return { fill: 0x6b5a30, accent: 0xd4b060, label: '#fff0c8', pattern: 'desert' };
        }
        if (col >= 3) {
            return { fill: 0x1a3d5c, accent: 0x4a9fd4, label: '#d0ecff', pattern: 'water' };
        }
        return { fill: 0x1e4a32, accent: 0x5ecf8a, label: '#d8ffe8', pattern: 'grass' };
    }
    if (row === 1) {
        if (col === 1) {
            return { fill: 0x6b5a30, accent: 0xd4b060, label: '#fff0c8', pattern: 'desert' };
        }
        if (col >= 4) {
            return { fill: 0x1a3d5c, accent: 0x4a9fd4, label: '#d0ecff', pattern: 'water' };
        }
        return { fill: 0x2a5238, accent: 0x4aa86a, label: '#d0f0d8', pattern: 'grass' };
    }
    if (row === 2) {
        if (col === 2) {
            return { fill: 0x2d4a28, accent: 0x6aaa50, label: '#e0ffd0', pattern: 'grass' };
        }
        if (col === 3) {
            return { fill: 0x3a4a30, accent: 0x8ab060, label: '#f0ffe0', pattern: 'road' };
        }
        if (col === 0 || col === 1) {
            return { fill: 0x6b5a30, accent: 0xd4b060, label: '#fff0c8', pattern: 'desert' };
        }
        return { fill: 0x2a5238, accent: 0x4aa86a, label: '#d0f0d8', pattern: 'grass' };
    }
    if (row === 3) {
        if (col === 0) {
            return { fill: 0x2a5238, accent: 0x4aa86a, label: '#d0f0d8', pattern: 'grass' };
        }
        if (col >= 3) {
            return { fill: 0x1a3d5c, accent: 0x4a9fd4, label: '#d0ecff', pattern: 'water' };
        }
        return { fill: 0x1e4a32, accent: 0x5ecf8a, label: '#d8ffe8', pattern: 'grass' };
    }
    if (col >= 3) {
        return { fill: 0x1a3d5c, accent: 0x4a9fd4, label: '#d0ecff', pattern: 'water' };
    }
    return { fill: 0x4a3020, accent: 0xb86830, label: '#ffd8b0', pattern: 'desert' };
}

export default class WorldMapOverlay {
    constructor(scene) {
        this.scene = scene;
        this.meta = scene.worldMapJson;
        this.isOpen = false;
        this.container = null;
        this.playerMarker = null;
        this.playerPulse = null;
        this.regionHighlight = null;
        this._layout = null;
        this._pulseTween = null;
    }

    toggle() {
        if (this.isOpen) this.hide();
        else this.show();
    }

    show() {
        if (this.isOpen || !this.meta?.chunks?.length) return;

        this.hide();
        this.isOpen = true;
        this.scene.movementEnabled = false;

        const cam = this.scene.cameras.main;
        const { width, height, centerX, centerY } = cam;
        const cols = this.meta.gridCols || 5;
        const rows = this.meta.gridRows || 5;

        const panelW = Math.min(width * 0.94, 1100);
        const panelH = Math.min(height * 0.9, 720);
        const panelX = centerX - panelW / 2;
        const panelY = centerY - panelH / 2;

        const sidebarW = Math.min(220, panelW * 0.22);
        const headerH = 64;
        const footerH = 36;
        const mapPad = 16;
        const mapX = panelX + sidebarW + mapPad;
        const mapY = panelY + headerH + mapPad;
        const mapW = panelW - sidebarW - mapPad * 2;
        const mapH = panelH - headerH - footerH - mapPad * 2;
        const gap = 6;
        const cellW = Math.floor((mapW - gap * (cols - 1)) / cols);
        const cellH = Math.floor((mapH - gap * (rows - 1)) / rows);

        this._layout = {
            originX: mapX,
            originY: mapY,
            cellW,
            cellH,
            gap,
            cols,
            rows,
            panelX,
            panelY,
            panelW,
            panelH,
            sidebarW
        };

        this.container = this.scene.add.container(0, 0);
        this.container.setScrollFactor(0);
        this.container.setDepth(20000);
        this.container.setAlpha(0);

        this.drawBackdrop(width, height, centerX, centerY);
        this.drawMainPanel(panelX, panelY, panelW, panelH);
        this.drawHeader(centerX, panelY, panelW);
        this.drawSidebar(panelX, panelY, sidebarW, panelH, headerH, footerH);
        this.drawMapGrid();
        this.drawRoadNetwork();
        this.drawLandmarks();
        this.drawCompass(panelX + panelW - 52, panelY + headerH + 16);

        this.playerMarker = this.scene.add.graphics();
        this.playerPulse = this.scene.add.graphics();
        this.container.add(this.playerPulse);
        this.container.add(this.playerMarker);
        this.refreshPlayerMarker();
        this.startPlayerPulse();

        this.drawFooter(centerX, panelY + panelH - footerH / 2 - 4);

        this.scene.tweens.add({
            targets: this.container,
            alpha: 1,
            duration: 220,
            ease: 'Power2'
        });
    }

    drawBackdrop(width, height, centerX, centerY) {
        const veil = this.scene.add.rectangle(centerX, centerY, width, height, 0x020408, 0.88);
        this.container.add(veil);

        const vignette = this.scene.add.graphics();
        vignette.fillStyle(0x000000, 0.35);
        vignette.fillRect(0, 0, width, 40);
        vignette.fillRect(0, height - 40, width, 40);
        this.container.add(vignette);
    }

    drawMainPanel(x, y, w, h) {
        const frame = this.scene.add.graphics();
        frame.fillStyle(0x0c1218, 0.98);
        frame.fillRoundedRect(x, y, w, h, 14);
        frame.lineStyle(3, 0x2a5570, 1);
        frame.strokeRoundedRect(x, y, w, h, 14);
        frame.lineStyle(1, 0x4cc9f0, 0.55);
        frame.strokeRoundedRect(x + 5, y + 5, w - 10, h - 10, 10);
        this.container.add(frame);
    }

    drawHeader(centerX, panelY, panelW) {
        const title = this.scene.add.text(centerX + this._layout.sidebarW * 0.35, panelY + 22, '世 界 地 图', {
            fontSize: '28px',
            fill: '#4cc9f0',
            fontFamily: UIStyleHelper.PIXEL_FONT,
            fontStyle: 'bold',
            stroke: '#001820',
            strokeThickness: 3
        }).setOrigin(0.5);
        this.container.add(title);

        const line = this.scene.add.graphics();
        line.lineStyle(1, 0x4cc9f0, 0.35);
        line.lineBetween(centerX - panelW * 0.28, panelY + 46, centerX + panelW * 0.32, panelY + 46);
        this.container.add(line);
    }

    drawSidebar(panelX, panelY, sidebarW, panelH, headerH, footerH) {
        const x = panelX + 12;
        const y = panelY + headerH + 8;
        const h = panelH - headerH - footerH - 16;

        const box = this.scene.add.graphics();
        box.fillStyle(0x081018, 0.92);
        box.fillRoundedRect(x, y, sidebarW - 20, h, 8);
        box.lineStyle(1, 0x335566, 0.9);
        box.strokeRoundedRect(x, y, sidebarW - 20, h, 8);
        this.container.add(box);

        const info = this.getPlayerMapInfo();
        const regionName = info.regionName || '未知区域';

        const lines = [
            { label: '当前位置', value: regionName, highlight: true },
            { label: '世界坐标', value: `${info.globalTileX}, ${info.globalTileY}` },
            { label: '所在区块', value: `${info.gridX} , ${info.gridY}` },
            { label: '世界规模', value: `${this.meta.gridCols}×${this.meta.gridRows} 区域` }
        ];

        let ty = y + 22;
        lines.forEach((line, index) => {
            this.container.add(this.scene.add.text(x + 14, ty, line.label, {
                fontSize: '12px',
                fill: '#668899',
                fontFamily: UIStyleHelper.PIXEL_FONT
            }));

            this.container.add(this.scene.add.text(x + 14, ty + 16, line.value, {
                fontSize: index === 0 ? '18px' : '14px',
                fill: line.highlight ? '#ffee88' : '#cce8ff',
                fontFamily: UIStyleHelper.PIXEL_FONT,
                fontStyle: index === 0 ? 'bold' : 'normal',
                wordWrap: { width: sidebarW - 36 }
            }));

            ty += index === 0 ? 52 : 40;
        });

        ty += 8;
        this.container.add(this.scene.add.text(x + 14, ty, '图例', {
            fontSize: '13px',
            fill: '#4cc9f0',
            fontFamily: UIStyleHelper.PIXEL_FONT,
            fontStyle: 'bold'
        }));

        const legend = [
            { color: 0xff5566, text: '● 你的位置' },
            { color: 0x4cc9f0, text: '■ 城镇' },
            { color: 0x88cc88, text: '■ 村庄' },
            { color: 0xffaa44, text: '■ 洞穴' }
        ];

        legend.forEach((item, i) => {
            this.container.add(this.scene.add.text(x + 14, ty + 22 + i * 22, item.text, {
                fontSize: '12px',
                fill: '#aabbc8',
                fontFamily: UIStyleHelper.PIXEL_FONT
            }));
        });
    }

    drawMapGrid() {
        const { originX, originY, cellW, cellH, gap, cols, rows } = this._layout;
        const chunkMap = new Map(this.meta.chunks.map((c) => [`${c.gridX}_${c.gridY}`, c]));

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const x = originX + col * (cellW + gap);
                const y = originY + row * (cellH + gap);
                const chunk = chunkMap.get(`${col}_${row}`);
                this.drawRegionCell(x, y, cellW, cellH, chunk, col, row);
            }
        }
    }

    drawRegionCell(x, y, cellW, cellH, chunk, col, row) {
        const style = getRegionStyle(row, col);
        const g = this.scene.add.graphics();

        g.fillStyle(style.fill, 1);
        g.fillRoundedRect(x, y, cellW, cellH, 6);
        g.lineStyle(2, style.accent, 0.55);
        g.strokeRoundedRect(x + 1, y + 1, cellW - 2, cellH - 2, 5);

        this.drawCellPattern(g, x, y, cellW, cellH, style.pattern, style.accent);
        this.container.add(g);

        const coord = this.scene.add.text(x + 10, y + 8, `${col},${row}`, {
            fontSize: '11px',
            fill: '#ffffff55',
            fontFamily: UIStyleHelper.PIXEL_FONT
        });
        this.container.add(coord);

        if (chunk?.name) {
            const nameBg = this.scene.add.graphics();
            nameBg.fillStyle(0x000000, 0.45);
            nameBg.fillRoundedRect(x + 8, y + cellH - 38, cellW - 16, 30, 4);
            this.container.add(nameBg);

            this.container.add(this.scene.add.text(
                x + cellW / 2,
                y + cellH - 22,
                chunk.name,
                {
                    fontSize: cellW > 130 ? '15px' : '13px',
                    fill: style.label,
                    fontFamily: UIStyleHelper.PIXEL_FONT,
                    fontStyle: 'bold',
                    align: 'center',
                    stroke: '#000000',
                    strokeThickness: 2,
                    wordWrap: { width: cellW - 20 }
                }
            ).setOrigin(0.5));
        }
    }

    drawCellPattern(g, x, y, w, h, pattern, accent) {
        g.fillStyle(accent, 0.12);

        if (pattern === 'grass') {
            for (let i = 0; i < 8; i++) {
                const px = x + 12 + (i % 4) * (w / 4);
                const py = y + 18 + Math.floor(i / 4) * (h / 3);
                g.fillCircle(px, py, 3);
            }
        } else if (pattern === 'desert') {
            for (let i = 0; i < 5; i++) {
                const px = x + 16 + i * (w / 5);
                g.fillEllipse(px, y + h * 0.35, 14, 5);
            }
        } else if (pattern === 'road') {
            g.lineStyle(2, accent, 0.25);
            g.lineBetween(x + w * 0.2, y + h / 2, x + w * 0.8, y + h / 2);
            g.lineBetween(x + w / 2, y + h * 0.2, x + w / 2, y + h * 0.8);
        } else if (pattern === 'ruin') {
            g.lineStyle(1, accent, 0.35);
            for (let i = 0; i < 4; i++) {
                g.strokeRect(x + 14 + i * 18, y + 20, 10, h - 50);
            }
        } else if (pattern === 'water') {
            for (let i = 0; i < 6; i++) {
                const px = x + 14 + (i % 3) * (w / 3);
                const py = y + 22 + Math.floor(i / 3) * (h / 2.5);
                g.fillEllipse(px, py, 18, 6);
            }
        }
    }

    drawRoadNetwork() {
        const { originX, originY, cellW, cellH, gap, cols, rows } = this._layout;
        const roads = this.scene.add.graphics();
        roads.lineStyle(3, 0xffee88, 0.22);

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols - 1; col++) {
                const x1 = originX + col * (cellW + gap) + cellW;
                const y1 = originY + row * (cellH + gap) + cellH / 2;
                const x2 = x1 + gap;
                roads.lineBetween(x1, y1, x2, y1);
            }
        }
        for (let col = 0; col < cols; col++) {
            for (let row = 0; row < rows - 1; row++) {
                const x1 = originX + col * (cellW + gap) + cellW / 2;
                const y1 = originY + row * (cellH + gap) + cellH;
                const y2 = y1 + gap;
                roads.lineBetween(x1, y1, x1, y2);
            }
        }
        this.container.add(roads);
    }

    drawLandmarks() {
        if (!this._layout || !this.meta) return;

        const { originX, originY, cellW, cellH, gap } = this._layout;
        const chunkW = this.meta.chunkWidth;
        const chunkH = this.meta.chunkHeight;

        WORLD_LANDMARKS.forEach((poi) => {
            const cellX = originX + poi.gridX * (cellW + gap);
            const cellY = originY + poi.gridY * (cellH + gap);
            const px = cellX + (poi.localTileX / chunkW) * cellW;
            const py = cellY + (poi.localTileY / chunkH) * cellH;

            const colors = { town: 0x4cc9f0, village: 0x66cc88, cave: 0xffaa44 };
            const color = colors[poi.type] || 0xffffff;

            const pin = this.scene.add.graphics();
            pin.fillStyle(color, 1);
            pin.fillCircle(px, py - 4, 7);
            pin.fillTriangle(px - 5, py - 2, px + 5, py - 2, px, py + 8);
            pin.lineStyle(2, 0xffffff, 0.9);
            pin.strokeCircle(px, py - 4, 7);
            this.container.add(pin);

            const tag = this.scene.add.graphics();
            tag.fillStyle(0x000000, 0.72);
            tag.fillRoundedRect(px - 36, py + 10, 72, 22, 4);
            tag.lineStyle(1, color, 0.8);
            tag.strokeRoundedRect(px - 36, py + 10, 72, 22, 4);
            this.container.add(tag);

            this.container.add(this.scene.add.text(px, py + 21, poi.label, {
                fontSize: '11px',
                fill: '#ffffff',
                fontFamily: UIStyleHelper.PIXEL_FONT,
                align: 'center'
            }).setOrigin(0.5));
        });
    }

    drawCompass(x, y) {
        const g = this.scene.add.graphics();
        g.fillStyle(0x101820, 0.85);
        g.fillCircle(x, y, 28);
        g.lineStyle(2, 0x4cc9f0, 0.8);
        g.strokeCircle(x, y, 28);
        g.fillStyle(0xff6666, 1);
        g.fillTriangle(x, y - 16, x - 7, y + 8, x + 7, y + 8);
        g.fillStyle(0x888888, 1);
        g.fillTriangle(x, y + 16, x - 7, y - 4, x + 7, y - 4);
        this.container.add(g);
        this.container.add(this.scene.add.text(x, y - 34, 'N', {
            fontSize: '12px',
            fill: '#ff8888',
            fontFamily: UIStyleHelper.PIXEL_FONT,
            fontStyle: 'bold'
        }).setOrigin(0.5));
    }

    drawFooter(centerX, y) {
        this.container.add(this.scene.add.text(centerX, y, '按  M  或  ESC  关闭地图', {
            fontSize: '14px',
            fill: '#8899aa',
            fontFamily: UIStyleHelper.PIXEL_FONT
        }).setOrigin(0.5));
    }

    getPlayerMapInfo() {
        const sprite = this.scene.playerSprite;
        if (!sprite) {
            return { globalTileX: 0, globalTileY: 0, gridX: 0, gridY: 0, regionName: '' };
        }

        const { globalTileX, globalTileY } = pixelToGlobalTile(this.meta, sprite.x, sprite.y);
        const { gridX, gridY } = globalTileToChunk(this.meta, globalTileX, globalTileY);
        const entry = findChunkEntry(this.meta, gridX, gridY);

        return {
            globalTileX,
            globalTileY,
            gridX,
            gridY,
            regionName: entry?.name || this.scene.chunkManager?.getRegionName?.(`${gridX}_${gridY}`) || ''
        };
    }

    refreshPlayerMarker() {
        if (!this.isOpen || !this.playerMarker || !this._layout || !this.scene.playerSprite) {
            return;
        }

        const sprite = this.scene.playerSprite;
        const { globalTileX, globalTileY } = pixelToGlobalTile(this.meta, sprite.x, sprite.y);
        const { gridX, gridY, localTileX, localTileY } = globalTileToChunk(this.meta, globalTileX, globalTileY);
        const { originX, originY, cellW, cellH, gap } = this._layout;

        const cellX = originX + gridX * (cellW + gap);
        const cellY = originY + gridY * (cellH + gap);
        const px = cellX + (localTileX / this.meta.chunkWidth) * cellW;
        const py = cellY + (localTileY / this.meta.chunkHeight) * cellH;

        if (this.regionHighlight) {
            this.regionHighlight.destroy();
        }

        const highlight = this.scene.add.graphics();
        highlight.lineStyle(3, 0xffee44, 1);
        highlight.strokeRoundedRect(cellX + 2, cellY + 2, cellW - 4, cellH - 4, 5);
        highlight.lineStyle(1, 0xffffff, 0.5);
        highlight.strokeRoundedRect(cellX + 6, cellY + 6, cellW - 12, cellH - 12, 4);
        this.regionHighlight = highlight;
        this.container.add(highlight);

        this.playerMarker.clear();
        this.playerMarker.fillStyle(0xff3344, 1);
        this.playerMarker.fillCircle(px, py, 8);
        this.playerMarker.lineStyle(3, 0xffffff, 1);
        this.playerMarker.strokeCircle(px, py, 8);
        this.playerMarker.fillStyle(0xffffff, 1);
        this.playerMarker.fillCircle(px, py, 3);

        if (this.playerPulse) {
            this.playerPulse.clear();
            this.playerPulse.lineStyle(2, 0xff5566, 0.8);
            this.playerPulse.strokeCircle(px, py, 14);
        }
    }

    startPlayerPulse() {
        if (!this.playerPulse) return;

        this._pulseTween = this.scene.tweens.add({
            targets: this.playerPulse,
            alpha: { from: 0.85, to: 0.2 },
            duration: 700,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    hide() {
        if (!this.isOpen) return;

        this.isOpen = false;
        this.scene.movementEnabled = true;

        if (this._pulseTween) {
            this._pulseTween.stop();
            this._pulseTween = null;
        }

        if (this.container) {
            this.container.destroy(true);
            this.container = null;
        }

        this.playerMarker = null;
        this.playerPulse = null;
        this.regionHighlight = null;
        this._layout = null;
    }

    destroy() {
        this.hide();
    }
}
